import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericField, GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import type { SelectOption } from '@neo/primitives/select'
import { ReactComponent as IcMcp } from '@shared/assets/icons/ic-mcp.svg'
import { useExternalOrgAppsQuery } from 'v2/pages/settings/integrations/queries'
import { getAccountIdentifier } from 'v2/pages/settings/integrations/utils/account-identifier'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { mcpAppValidation } from '../../config/mcp-tool-validation'
import { DEFAULT_PYTHON_CODE } from '../../config/nodes.config'
import type { McpToolNodeData, SidebarComponentProps } from '../../types'
import { GenericSidebarComponent } from '../sidebar.base'
import { MCP_APP_FIELDS, MCP_CODE_FIELDS } from './mcp-tool-config'
import { McpCustomCodeFields } from './mcp-tool-custom-code'
import {
  FieldLabel,
  type McpToolFormValues,
  McpToolInputs,
  mcpToolFormValues,
  resetToolInputs,
} from './mcp-tool-inputs'

interface ConnectedApps {
  apps: RExternalOrgApp[] | undefined
  isLoading: boolean
  isError: boolean
}

// Custom MCP servers can come without a logo.
const AppIcon = ({ logoUrl }: { logoUrl?: string }) =>
  logoUrl ? (
    <img src={logoUrl} alt="" className="size-4 object-contain" />
  ) : (
    <IcMcp className="size-4" />
  )

const appOption = (app: RExternalOrgApp): SelectOption => {
  // Several connections can share one app; the account tells them apart.
  const account = getAccountIdentifier(app)
  return {
    value: app.id,
    label: account ? `${app.name} (${account})` : app.name,
    icon: <AppIcon logoUrl={app.logo_url} />,
  }
}

const appPlaceholder = ({ isLoading, isError }: ConnectedApps, hasOptions: boolean) => {
  if (isLoading) return 'Loading apps…'
  if (hasOptions) return 'Select an app'
  return isError ? 'Could not load connected apps' : 'No connected apps'
}

const appField = (connected: ConnectedApps): GenericField<McpToolFormValues> => {
  const { apps, isLoading } = connected
  const options = (apps ?? []).map(appOption)
  // The Select also reports a re-pick of the chosen app; this holds the value before the change.
  let previous: string | null | undefined
  return {
    name: 'org_app_uuid',
    componentType: 'select',
    label: <FieldLabel required>App</FieldLabel>,
    zodValidation: mcpAppValidation.org_app_uuid,
    getDynamicProps: (values) => {
      const saved = values.org_app_uuid
      // Kept as a disabled option so the node still names the app it was built for.
      const missing = !!saved && !options.some((option) => option.value === saved)
      return {
        inputProps: {
          options: missing
            ? [
                ...options,
                {
                  value: saved,
                  label: values.app_name || saved,
                  icon: <AppIcon />,
                  disabled: true,
                },
              ]
            : options,
          loading: isLoading,
          placeholder: appPlaceholder(connected, options.length > 0),
          emptyPlaceholder: options.length ? undefined : 'No connected apps',
          helperText:
            isLoading || options.length
              ? ''
              : 'Connect an app or MCP server in Settings → Integrations',
          // Without a list (embed, failed load) nothing says the connection is gone.
          error: missing && apps ? 'This connection is no longer available' : undefined,
        },
      }
    },
    eventDataExtractor: (value: string, form) => {
      previous = form?.state.values.org_app_uuid
      return value
    },
    afterOnChange: (values, _index, form) => {
      if (!form || values.org_app_uuid === previous) return
      const app = apps?.find((row) => row.id === values.org_app_uuid)
      form.setFieldValue('app_provider', app?.provider ?? '')
      form.setFieldValue('app_name', app?.name ?? '')
      form.setFieldValue('tool_name', '')
      form.setFieldValue('tool_schema', null)
      resetToolInputs(form, {})
    },
  }
}

const sourceField = (): GenericField<McpToolFormValues> => {
  let previous: McpToolFormValues['source']
  return {
    name: 'source',
    componentType: 'select',
    label: 'Source',
    inputProps: {
      options: [
        { label: 'App', value: 'app' },
        { label: 'Custom code', value: 'custom_code', disabled: isEmbedMode() },
      ],
      'data-testid': 'select-mcp-source',
    },
    eventDataExtractor: (value, form) => {
      previous = form?.state.values.source
      return value
    },
    afterOnChange: (values, _index, form) => {
      if (!form || values.source === previous) return
      const inactiveFields = values.source === 'custom_code' ? MCP_APP_FIELDS : MCP_CODE_FIELDS
      for (const name of inactiveFields) {
        form.deleteField(name)
        // Empty values prevent remounting fields from restoring the original node defaults.
        form.setFieldValue(name, name.endsWith('_schema') ? null : '', { dontUpdateMeta: true })
      }
      resetToolInputs(form, {})
      if (values.source === 'custom_code' && !values.code_body) {
        // The code field has not mounted yet; let it initialize its own validation metadata.
        form.setFieldValue('code_body', DEFAULT_PYTHON_CODE, { dontUpdateMeta: true })
      }
    },
  }
}

const McpSourceFields = ({
  form,
  connected,
  nodeVars,
}: {
  form: GenericFormType<McpToolFormValues>
  connected: ConnectedApps
  nodeVars: string[]
}) => {
  const source = form.useStore((state) => state.values.source ?? 'app')
  return (
    <div key={source} className="col-span-2 flex flex-col gap-4">
      {source === 'custom_code' ? (
        <McpCustomCodeFields form={form} nodeVars={nodeVars} />
      ) : (
        <FieldsRenderer form={form} fields={[appField(connected)]} />
      )}
      <McpToolInputs form={form} apps={connected.apps} nodeVars={nodeVars} />
    </div>
  )
}

const fields = (
  nodeVars: string[],
  connected: ConnectedApps,
): GenericFieldConfig<McpToolFormValues> => [
  sourceField(),
  {
    name: 'source_section',
    type: 'basic',
    component: ({ form }) => (
      <McpSourceFields form={form} connected={connected} nodeVars={nodeVars} />
    ),
  },
]

export const McpToolSidebarComponent = (props: SidebarComponentProps<McpToolNodeData>) => {
  const { data: apps, isLoading, isError } = useExternalOrgAppsQuery(true)
  const formFields = fields(props.nodeVars, { apps, isLoading, isError })

  return <GenericSidebarComponent fields={formFields} formValues={mcpToolFormValues} {...props} />
}
